// MultiStepForm.js
import React, { useEffect, useState } from "react";
import { useRef } from "react";
import {
  Container,
  Form,
  Button,
  ProgressBar,
  Nav,
  Alert,
} from "react-bootstrap";
import SignaturePad from "react-signature-canvas"; // Import the SignaturePad component
import "./Signature.css";
import { connect } from "react-redux";
import { getSasToken } from "../../store/thunk/user";
import { dataURLtoBlob } from "../../utils/dataURLtoBlob";
import { uploadDocumentAndGetUrl } from "../../utils/uploadDocument";
import { updateForm } from "../../utils/updateForm";
import { toast } from "react-toastify";
import { triggerButtonClick } from "../../utils/triggerLibraryButton";
import GdprPolicyDownload from "./Download/GdprPolicyDownload";
import { Tabs, Tab, Box } from "@mui/material";
import moment from "moment";

const GdprPolicy = ({
  currentDocument,
  documentList,
  user,
  sasToken,
  ipAddress,
  getSasToken,
}) => {
  const [step, setStep] = useState(1);
  const [value, setValue] = useState(0);
  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };
  const [progress, setProgress] = useState(0); // Progress percentage for each step
  const [formData, setFormData] = useState({
    title: "",
    firstName: "",
    surName: "",
    documentSignature: "",
    signedDate: "",
    consent: false,
    signature: "", // Store the signature data as a string
  });
  const [errors, setErrors] = useState({});
  const sigPadRef = useRef(null); // Reference to the SignaturePad component
  const documentSigPadRef = useRef(null); // Reference to the SignaturePad component
  useEffect(() => {
    setFormData(currentDocument);
    if (currentDocument?.step) {
      setStep(currentDocument?.step);
    }
    if (currentDocument?.progress) {
      setProgress(currentDocument?.progress);
    }
    if (currentDocument?.progress === 100) {
      getSasToken();
    }
  }, []);
  // Function to handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    setErrors({ ...errors, [name]: "" });
  };

  // Function to validate each step
  const validateStep = () => {
    let formErrors = {};
    if (step === 1) {
      if (!formData.title) formErrors.title = "This field is required";
      if (!formData.firstName) formErrors.firstName = "This field is required";
      if (!formData.surName) formErrors.surName = "This field is required";
      if (documentSigPadRef.current.isEmpty()) {
        formErrors.documentSignature = "This field is required"; // Check if the signature pad is empty
      }
    } else if (step === 2) {
      if (!formData.consent) formErrors.consent = "You must agree to the terms";
      if (sigPadRef.current.isEmpty())
        formErrors.signature = "Signature is required"; // Check if the signature pad is empty
    }
    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  // Function to go to the next step
  const nextStep = async () => {
    if (validateStep()) {
      const documentSignatureImage = documentSigPadRef.current
        .getTrimmedCanvas()
        .toDataURL("image/png");
      const signatureBlob = dataURLtoBlob(documentSignatureImage);
      // Create a FormData object
      const formDataSign = new FormData();
      // Append the Blob as a file to the FormData
      // The second argument is the file name, which you can customize as needed
      formDataSign.append(
        "file",
        signatureBlob,
        `${[currentDocument["key"]]}.png`
      );
      // Send the FormData using postMultiPartFormData
      const documentUrl =
        (await uploadDocumentAndGetUrl(formDataSign, user?.token)) || "";
      if (documentUrl) {
        const payload = {
          [currentDocument["key"]]: {
            name: currentDocument?.name,
            ...formData,
            documentSignature: documentUrl,
            progress: progress + 50,
            step: step + 1,
          },
        };
        updateForm(payload, documentList, user);
        setStep(step + 1);
        setProgress(progress + 50);
      } else {
        toast.error(
          "Something went wrong while upload signature. please try again."
        );
      }
    }
  };

  // Function to go to the previous step
  const prevStep = () => {
    setStep(step - 1);
    setProgress(progress - 50);
  };

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateStep()) {
      // Get the signature data as a base64 image
      const signatureImage = sigPadRef.current
        .getTrimmedCanvas()
        .toDataURL("image/png");
      setFormData({
        ...formData,
        signature: signatureImage,
      });
      const signatureBlob = dataURLtoBlob(signatureImage);
      // Create a FormData object
      const formDataSign = new FormData();
      // Append the Blob as a file to the FormData
      // The second argument is the file name, which you can customize as needed
      formDataSign.append(
        "file",
        signatureBlob,
        `${[currentDocument["key"]]}.png`
      );
      // Send the FormData using postMultiPartFormData
      const documentUrl =
        (await uploadDocumentAndGetUrl(formDataSign, user?.token)) || "";
      if (documentUrl) {
        const payload = {
          [currentDocument["key"]]: {
            name: currentDocument?.name,
            ...formData,
            progress: 100,
            step: step,
            signature: documentUrl,
            signDate: new Date(),
            ipAddress: ipAddress,
          },
        };
        updateForm(payload, documentList, user);
        toast.success("Successfully filled the form.");
        triggerButtonClick();
      } else {
        toast.error(
          "Something went wrong while upload signature. please try again."
        );
      }
      // Additional submit logic here, such as sending formData to the server
    }
  };
  // Clear the signature pad
  const clearSignature = () => {
    sigPadRef.current.clear();
    setFormData({ ...formData, signature: "" });
  };
  // Clear the signature pad
  const clearDocumentSignature = () => {
    documentSigPadRef.current.clear();
    setFormData({ ...formData, documentSignature: "" });
  };
  return (
    <Container className="mt-5">
      {currentDocument?.progress === 100 ? (
        <>
          <GdprPolicyDownload user={documentList?.userDetail} data={currentDocument} sasToken={sasToken} />
          <Box sx={{ width: "100%" }}>
            <Tabs
              value={value}
              onChange={handleTabChange}
              aria-label="Personal Information"
              className="tab mt-2"
            >
              <Tab label="Personal Information" />
            </Tabs>
            {value === 0 && (
              <div className="container">
                <div className="row">
                  <h2>Personal Information</h2>
                  <ul class="list-group">
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Title</strong>{" "}
                      <span>{currentDocument?.title || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>First Name</strong>{" "}
                      <span>{currentDocument?.firstName || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Surname</strong>{" "}
                      <span>{currentDocument?.surName || "--"}</span>
                    </li>
                    <li
                      class="list-group-item d-flex"
                      style={{ textAlign: "justify" }}
                    >
                      <strong>
                        The Company is committed to protecting the privacy and
                        security of your personal information. This privacy
                        notice describes how we collect and use personal
                        information about you during and after your working
                        relationship with us, in accordance with the General
                        Data Protection Regulation (GDPR). It applies to all
                        employees, workers and contractors. The company is a
                        "data controller". This means that we are responsible
                        for deciding how we hold and use personal information
                        about you. We are required under data protection
                        legislation to notify you of the information contained
                        within this document. This notice applies to current and
                        former employees, workers and contractors. This notice
                        does not form part of any contract of employment or
                        other contract to provide services. We may update this
                        notice at any time. It is important that you read this
                        notice, together with any other privacy notice we may
                        provide on specific occasions when we are collecting or
                        processing personal information about you, so that you
                        are aware of how and why we are using such information.
                        Data protection principles We will comply with data
                        protection law. This says that the personal information
                        we hold about you must be: 1. Used lawfully, fairly and
                        in a transparent way. 2. Collected only for valid
                        purposes that we have clearly explained to you and not
                        used in any way that is incompatible with those
                        purposes. 3. Relevant to the purposes we have told you
                        about and limited only to those purposes. 4. Accurate
                        and kept up to date. 5. Kept only as long as necessary
                        for the purposes we have told you about. 6. Kept
                        securely. The kind of information we hold about you
                        Personal data, or personal information, means any
                        information about an individual from which that person
                        can be identified. It does not include data where the
                        identity has been removed (anonymous data). There are
                        "special categories" of more sensitive personal data
                        which require a higher level of protection. We will
                        collect, store, and use the following categories of
                        personal information about you: • Personal contact
                        details such as name, title, addresses, telephone
                        numbers, and personal email addresses. • Date of birth.
                        • Gender. • Marital status and dependants. • Next of kin
                        and emergency contact information. • National Insurance
                        number. • Bank account details, payroll records and tax
                        status information. • Salary, annual leave, pension and
                        benefits information. • Start date. • Location of
                        employment or workplace. • Copy of driving licence. •
                        Recruitment information (including copies of right to
                        work documentation, references and other information
                        included in a CV or cover letter or as part of the
                        application process). • Employment records (including
                        job titles, work history, working hours, training
                        records and professional memberships). • Compensation
                        history. • Performance information. • Disciplinary and
                        grievance information. • CCTV footage (where applicable)
                        and other information obtained through electronic means
                        such as swipecard records. • Information about your use
                        of our information and communications systems. •
                        Photographs. We may also collect, store and use the
                        following "special categories" of more sensitive
                        personal information: • Information about your race or
                        ethnicity, religious beliefs, sexual orientation and
                        political opinions. • Trade union membership. •
                        Information about your health, including any medical
                        condition, health and sickness records. • Genetic
                        information and biometric data. • Information about
                        criminal convictions and offences. How is your personal
                        information collected? We collect personal information
                        about employees, workers and contactors through the
                        application and recruitment process, either directly
                        from candidates or sometimes from an employment agency
                        or background check provider. We may sometimes collect
                        additional information from third parties including
                        former employers, credit reference agencies (where
                        applicable) or other background check agencies. We will
                        collect additional personal information in the course of
                        job-related activities throughout the period of you
                        working for us. How we will use information about you We
                        will only use your personal information when the law
                        allows us to. Most commonly, we will use your personal
                        information in the following circumstances: 1. Where we
                        need to perform the contract we have entered into with
                        you. 2. Where we need to comply with a legal obligation.
                        3. Where it is necessary for our legitimate interests
                        (or those of a third party) and your interests and
                        fundamental rights do not override those interests. We
                        may also use your personal information in the following
                        situations, which are likely to be rare: 1. Where we
                        need to protect your interests (or someone else's
                        interests). 2. Where it is needed in the public interest
                        [or for official purposes]. Situations in which we will
                        use your personal information We need all the categories
                        of information as listed primarily to allow us to
                        perform our contract with you and to enable us to comply
                        with legal obligations. In some cases we may use your
                        personal information to pursue legitimate interests of
                        our own or those of third parties, provided your
                        interests and fundamental rights do not override those
                        interests. The situations in which we will process your
                        personal information are listed below. • Making a
                        decision about your recruitment or appointment. •
                        Determining the terms on which you work for us. •
                        Checking you are legally entitled to work in the UK. •
                        Paying you and, if you are an employee, deducting tax
                        and National Insurance contributions. • Providing
                        company benefits (where applicable). • Liaising with
                        your pension provider. • Administering the contract, we
                        have entered into with you. • Business management and
                        planning, including accounting and auditing. •
                        Conducting performance reviews, managing performance and
                        determining performance requirements. • Making decisions
                        about salary reviews and compensation. • Assessing
                        qualifications for a particular job or task, including
                        decisions about promotions. • Gathering evidence for
                        possible grievance or disciplinary hearings. • Making
                        decisions about your continued employment or engagement.
                        • Making arrangements for the termination of our working
                        relationship. • Education, training and development
                        requirements. • Dealing with legal disputes involving
                        you, or other employees, workers and contractors,
                        including accidents at work. • Ascertaining your fitness
                        to work. • Managing sickness absence. • Complying with
                        health and safety obligations. • To prevent fraud. • To
                        monitor your use of our information and communication
                        systems to ensure compliance with our IT policies. • To
                        ensure network and information security, including
                        preventing unauthorised access to our computer and
                        electronic communications systems and preventing
                        malicious software distribution. • To conduct data
                        analytics studies to review and better understand
                        employee retention and attrition rates. • Equal
                        opportunities monitoring. Some of the above grounds for
                        processing will overlap and there may be several grounds
                        which justify our use of your personal information. If
                        you fail to provide personal information If you fail to
                        provide certain information when requested, we may not
                        be able to perform the contract we have entered into
                        with you (such as paying you or providing a benefit), or
                        we may be prevented from complying with our legal
                        obligations (such as to ensure the health and safety of
                        our workers). Change of purpose We will only use your
                        personal information for the purposes for which we
                        collected it, unless we reasonably consider that we need
                        to use it for another reason and that reason is
                        compatible with the original purpose. If we need to use
                        your personal information for an unrelated purpose, we
                        will notify you and we will explain the legal basis
                        which allows us to do so. Please note that we may
                        process your personal information without your knowledge
                        or consent, in compliance with the above rules, where
                        this is required or permitted by law. How we use
                        particularly sensitive personal information "Special
                        categories" of particularly sensitive personal
                        information require higher levels of protection. We need
                        to have further justification for collecting, storing
                        and using this type of personal information. We have in
                        place an appropriate policy document and safeguards
                        which we are required by law to maintain when processing
                        such data. We may process special categories of personal
                        information in the following circumstances: 1. Where we
                        need to carry out our legal obligations or exercise
                        rights in connection with employment. 2. Where it is
                        needed in the public interest, such as for equal
                        opportunities monitoring or in relation to our pension
                        scheme. Less commonly, we may process this type of
                        information where it is needed in relation to legal
                        claims or where it is needed to protect your interests
                        (or someone else's interests) and you are not capable of
                        giving your consent, or where you have already made the
                        information public. We may also process such information
                        about members or former members in the course of
                        legitimate business activities Our obligations as an
                        employer We will use your particularly sensitive
                        personal information in the following ways: • We will
                        use information relating to leaves of absence, which may
                        include sickness absence or family related leaves, to
                        comply with employment and other laws. • We will use
                        information about your physical or mental health, or
                        disability status, to ensure your health and safety in
                        the workplace and to assess your fitness to work, to
                        provide appropriate workplace adjustments, to monitor
                        and manage sickness absence and to administer benefits.
                        • We will use information about your race or national or
                        ethnic origin, religious, philosophical or moral
                        beliefs, or your sexual life or sexual orientation, to
                        ensure meaningful equal opportunity monitoring and
                        reporting. • We will use trade union membership
                        information to pay trade union premiums, register the
                        status of a protected employee and to comply with
                        employment law obligations (where applicable). Do we
                        need your consent? We do not need your consent if we use
                        special categories of your personal information in
                        accordance with our written policy to carry out our
                        legal obligations or exercise specific rights in the
                        field of employment law. In limited circumstances, we
                        may approach you for your written consent to allow us to
                        process certain particularly sensitive data. If we do
                        so, we will provide you with full details of the
                        information that we would like and the reason we need
                        it, so that you can carefully consider whether you wish
                        to consent. You should be aware that it is not a
                        condition of your contract with us that you agree to any
                        request for consent from us. Information about criminal
                        convictions We may only use information relating to
                        criminal convictions where the law allows us to do so.
                        This will usually be where such processing is necessary
                        to carry out our obligations. Less commonly, we may use
                        information relating to criminal convictions where it is
                        necessary in relation to legal claims, where it is
                        necessary to protect your interests (or someone else's
                        interests) and you are not capable of giving your
                        consent, or where you have already made the information
                        public. We may also process such information about
                        members or former members in the course of legitimate
                        business activities. We will only collect information
                        about criminal convictions if it is appropriate given
                        the nature of the role and where we are legally able to
                        do so. Where appropriate, we will collect information
                        about criminal convictions as part of the recruitment
                        process or we may be notified of such information
                        directly by you in the course of you working for us.
                        Data sharing We may have to share your data with third
                        parties, including third-party service providers and
                        other entities in the group. We require third parties to
                        respect the security of your data and to treat it in
                        accordance with the law. We do not transfer your
                        personal information outside the EU. Why might you share
                        my personal information with third parties? We will
                        share your personal information with third parties where
                        required by law, where it is necessary to administer the
                        working relationship with you or where we have another
                        legitimate interest in doing so. Which third-party
                        service providers process my personal information?
                        "Third parties" includes third-party service providers
                        (including contractors and designated agents) and (where
                        applicable) other entities within our group. How secure
                        is my information with third-party service providers and
                        other entities in our group? All our third-party service
                        providers and other entities in the group are required
                        to take appropriate security measures to protect your
                        personal information in line with our policies. We do
                        not allow our third-party service providers to use your
                        personal data for their own purposes. We only permit
                        them to process your personal data for specified
                        purposes and in accordance with our instructions. When
                        might you share my personal information with other
                        entities in the group? We will share your personal
                        information with other entities in our group as part of
                        our regular reporting activities on company performance,
                        in the context of a business reorganisation or group
                        restructuring exercise, for system maintenance support
                        and hosting of data. What about other third parties? We
                        may share your personal information with other third
                        parties, for example in the context of the possible sale
                        or restructuring of the business. We may also need to
                        share your personal information with a regulator or to
                        otherwise comply with the law. Data security We have put
                        in place measures to protect the security of your
                        information. Third parties will only process your
                        personal information on our instructions and where they
                        have agreed to treat the information confidentially and
                        to keep it secure. We have put in place appropriate
                        security measures to prevent your personal information
                        from being accidentally lost, used or accessed in an
                        unauthorised way, altered or disclosed. In addition, we
                        limit access to your personal information to those
                        employees, agents, contractors and other third parties
                        who have a business need to know. They will only process
                        your personal information on our instructions and they
                        are subject to a duty of confidentiality. We have put in
                        place procedures to deal with any suspected data
                        security breach and will notify you and any applicable
                        regulator of a suspected breach where we are legally
                        required to do so. Data retention How long will you use
                        my information for? We will only retain your personal
                        information for as long as necessary to fulfil the
                        purposes we collected it for, including for the purposes
                        of satisfying any legal, accounting, or reporting
                        requirements. In order to determine the appropriate
                        retention period for personal data, we consider the
                        amount, nature, and sensitivity of the personal data,
                        the potential risk of harm from unauthorised use or
                        disclosure of your personal data, the purposes for which
                        we process your personal data and whether we can achieve
                        those purposes through other means, and the applicable
                        legal requirements. In some circumstances we may
                        anonymise your personal information so that it can no
                        longer be associated with you, in which case we may use
                        such information without further notice to you. Once you
                        are no longer an employee, worker or contractor of the
                        company we will retain and securely destroy your
                        personal information in accordance with applicable laws
                        and regulations. Rights of access, correction, erasure,
                        and restriction Your duty to inform us of changes It is
                        important that the personal information we hold about
                        you is accurate and current. Please keep us informed if
                        your personal information changes during your working
                        relationship with us. Your rights in connection with
                        personal information Under certain circumstances, by law
                        you have the right to: • Request access to your personal
                        information (commonly known as a "data subject access
                        request"). This enables you to receive a copy of the
                        personal information we hold about you and to check that
                        we are lawfully processing it. • Request correction of
                        the personal information that we hold about you. This
                        enables you to have any incomplete or inaccurate
                        information we hold about you corrected. • Request
                        erasure of your personal information. This enables you
                        to ask us to delete or remove personal information where
                        there is no good reason for us continuing to process it.
                        You also have the right to ask us to delete or remove
                        your personal information where you have exercised your
                        right to object to processing (see below). • Object to
                        processing of your personal information where we are
                        relying on a legitimate interest (or those of a third
                        party) and there is something about your particular
                        situation which makes you want to object to processing
                        on this ground. You also have the right to object where
                        we are processing your personal information for direct
                        marketing purposes. • Request the restriction of
                        processing of your personal information. This enables
                        you to ask us to suspend the processing of personal
                        information about you, for example if you want us to
                        establish its accuracy or the reason for processing it.
                        • Request the transfer of your personal information to
                        another party. If you want to review, verify, correct or
                        request erasure of your personal information, object to
                        the processing of your personal data, or request that we
                        transfer a copy of your personal information to another
                        party, please contact Rapid Resourcing Ltd in
                        writing. No fee usually required You will not have to
                        pay a fee to access your personal information (or to
                        exercise any of the other rights). However, we may
                        charge a reasonable fee if your request for access is
                        clearly unfounded or excessive. Alternatively, we may
                        refuse to comply with the request in such circumstances.
                        What we may need from you We may need to request
                        specific information from you to help us confirm your
                        identity and ensure your right to access the information
                        (or to exercise any of your other rights). This is
                        another appropriate security measure to ensure that
                        personal information is not disclosed to any person who
                        has no right to receive it. Right to withdraw consent In
                        the limited circumstances where you may have provided
                        your consent to the collection, processing and transfer
                        of your personal information for a specific purpose, you
                        have the right to withdraw your consent for that
                        specific processing at any time. In order to withdraw
                        your consent, please contact your Manager in writing
                        (including the legal basis for your belief that your
                        consent can be withdrawn). Once we have received
                        notification that you have withdrawn your consent, we
                        will no longer process your information for the purpose
                        or purposes you originally agreed to, unless we have
                        another legitimate basis for doing so in law. Changes to
                        this privacy notice We reserve the right to update this
                        privacy notice at any time, and we will provide you with
                        a new privacy notice when we make any substantial
                        updates. We may also notify you in other ways from time
                        to time about the processing of your personal
                        information. If you have any questions about this
                        privacy notice, please contact Rapid Resourcing
                        Ltd.
                        <br />
                        Applicant Name: {currentDocument?.firstName}{" "}
                        {currentDocument?.surName}
                      </strong>
                    </li>
                    <li className="list-group-item d-flex">
                      <div className="row">
                        <div className="col-12">
                          {currentDocument?.signature && (
                            <img
                              width={100}
                              src={`${currentDocument?.signature}?${sasToken}`}
                              alt="Signature"
                            />
                          )}
                        </div>
                      </div>
                    </li>
                    <li className="list-group-item d-flex">
                      Sign Date:{" "}
                      {moment(currentDocument?.signDate).format("DD-MM-YYYY")}
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </Box>
        </>
      ) : (
        <>
          <Nav variant="pills" className="justify-content-center mb-3">
            <Nav.Item>
              <Nav.Link
                className={step === 1 ? "text-light" : ""}
                active={step === 1}
              >
                Step 1
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className={step === 2 ? "text-light" : ""}
                active={step === 2}
              >
                Step 2
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <ProgressBar now={progress} className="mb-4" />

          <Form onSubmit={handleSubmit}>
            {step === 1 && (
              <div>
                <h3>Section 1: Personal Information</h3>
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    isInvalid={!!errors.title}
                    required
                  />
                  {errors.title && (
                    <Alert variant="danger">{errors.title}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    isInvalid={!!errors.firstName}
                    required
                  />
                  {errors.firstName && (
                    <Alert variant="danger">{errors.firstName}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Surname</Form.Label>
                  <Form.Control
                    type="text"
                    name="surName"
                    value={formData.surName}
                    onChange={handleChange}
                    isInvalid={!!errors.surName}
                    required
                  />
                  {errors.surName && (
                    <Alert variant="danger">{errors.surName}</Alert>
                  )}
                </Form.Group>
                <p style={{ textAlign: "justify" }}>
                  The Company is committed to protecting the privacy and
                  security of your personal information. This privacy notice
                  describes how we collect and use personal information about
                  you during and after your working relationship with us, in
                  accordance with the General Data Protection Regulation (GDPR).
                  It applies to all employees, workers and contractors. The
                  company is a "data controller". This means that we are
                  responsible for deciding how we hold and use personal
                  information about you. We are required under data protection
                  legislation to notify you of the information contained within
                  this document. This notice applies to current and former
                  employees, workers and contractors. This notice does not form
                  part of any contract of employment or other contract to
                  provide services. We may update this notice at any time. It is
                  important that you read this notice, together with any other
                  privacy notice we may provide on specific occasions when we
                  are collecting or processing personal information about you,
                  so that you are aware of how and why we are using such
                  information. Data protection principles We will comply with
                  data protection law. This says that the personal information
                  we hold about you must be: 1. Used lawfully, fairly and in a
                  transparent way. 2. Collected only for valid purposes that we
                  have clearly explained to you and not used in any way that is
                  incompatible with those purposes. 3. Relevant to the purposes
                  we have told you about and limited only to those purposes. 4.
                  Accurate and kept up to date. 5. Kept only as long as
                  necessary for the purposes we have told you about. 6. Kept
                  securely. The kind of information we hold about you Personal
                  data, or personal information, means any information about an
                  individual from which that person can be identified. It does
                  not include data where the identity has been removed
                  (anonymous data). There are "special categories" of more
                  sensitive personal data which require a higher level of
                  protection. We will collect, store, and use the following
                  categories of personal information about you: • Personal
                  contact details such as name, title, addresses, telephone
                  numbers, and personal email addresses. • Date of birth. •
                  Gender. • Marital status and dependants. • Next of kin and
                  emergency contact information. • National Insurance number. •
                  Bank account details, payroll records and tax status
                  information. • Salary, annual leave, pension and benefits
                  information. • Start date. • Location of employment or
                  workplace. • Copy of driving licence. • Recruitment
                  information (including copies of right to work documentation,
                  references and other information included in a CV or cover
                  letter or as part of the application process). • Employment
                  records (including job titles, work history, working hours,
                  training records and professional memberships). • Compensation
                  history. • Performance information. • Disciplinary and
                  grievance information. • CCTV footage (where applicable) and
                  other information obtained through electronic means such as
                  swipecard records. • Information about your use of our
                  information and communications systems. • Photographs. We may
                  also collect, store and use the following "special categories"
                  of more sensitive personal information: • Information about
                  your race or ethnicity, religious beliefs, sexual orientation
                  and political opinions. • Trade union membership. •
                  Information about your health, including any medical
                  condition, health and sickness records. • Genetic information
                  and biometric data. • Information about criminal convictions
                  and offences. How is your personal information collected? We
                  collect personal information about employees, workers and
                  contactors through the application and recruitment process,
                  either directly from candidates or sometimes from an
                  employment agency or background check provider. We may
                  sometimes collect additional information from third parties
                  including former employers, credit reference agencies (where
                  applicable) or other background check agencies. We will
                  collect additional personal information in the course of
                  job-related activities throughout the period of you working
                  for us. How we will use information about you We will only use
                  your personal information when the law allows us to. Most
                  commonly, we will use your personal information in the
                  following circumstances: 1. Where we need to perform the
                  contract we have entered into with you. 2. Where we need to
                  comply with a legal obligation. 3. Where it is necessary for
                  our legitimate interests (or those of a third party) and your
                  interests and fundamental rights do not override those
                  interests. We may also use your personal information in the
                  following situations, which are likely to be rare: 1. Where we
                  need to protect your interests (or someone else's interests).
                  2. Where it is needed in the public interest [or for official
                  purposes]. Situations in which we will use your personal
                  information We need all the categories of information as
                  listed primarily to allow us to perform our contract with you
                  and to enable us to comply with legal obligations. In some
                  cases we may use your personal information to pursue
                  legitimate interests of our own or those of third parties,
                  provided your interests and fundamental rights do not override
                  those interests. The situations in which we will process your
                  personal information are listed below. • Making a decision
                  about your recruitment or appointment. • Determining the terms
                  on which you work for us. • Checking you are legally entitled
                  to work in the UK. • Paying you and, if you are an employee,
                  deducting tax and National Insurance contributions. •
                  Providing company benefits (where applicable). • Liaising with
                  your pension provider. • Administering the contract, we have
                  entered into with you. • Business management and planning,
                  including accounting and auditing. • Conducting performance
                  reviews, managing performance and determining performance
                  requirements. • Making decisions about salary reviews and
                  compensation. • Assessing qualifications for a particular job
                  or task, including decisions about promotions. • Gathering
                  evidence for possible grievance or disciplinary hearings. •
                  Making decisions about your continued employment or
                  engagement. • Making arrangements for the termination of our
                  working relationship. • Education, training and development
                  requirements. • Dealing with legal disputes involving you, or
                  other employees, workers and contractors, including accidents
                  at work. • Ascertaining your fitness to work. • Managing
                  sickness absence. • Complying with health and safety
                  obligations. • To prevent fraud. • To monitor your use of our
                  information and communication systems to ensure compliance
                  with our IT policies. • To ensure network and information
                  security, including preventing unauthorised access to our
                  computer and electronic communications systems and preventing
                  malicious software distribution. • To conduct data analytics
                  studies to review and better understand employee retention and
                  attrition rates. • Equal opportunities monitoring. Some of the
                  above grounds for processing will overlap and there may be
                  several grounds which justify our use of your personal
                  information. If you fail to provide personal information If
                  you fail to provide certain information when requested, we may
                  not be able to perform the contract we have entered into with
                  you (such as paying you or providing a benefit), or we may be
                  prevented from complying with our legal obligations (such as
                  to ensure the health and safety of our workers). Change of
                  purpose We will only use your personal information for the
                  purposes for which we collected it, unless we reasonably
                  consider that we need to use it for another reason and that
                  reason is compatible with the original purpose. If we need to
                  use your personal information for an unrelated purpose, we
                  will notify you and we will explain the legal basis which
                  allows us to do so. Please note that we may process your
                  personal information without your knowledge or consent, in
                  compliance with the above rules, where this is required or
                  permitted by law. How we use particularly sensitive personal
                  information "Special categories" of particularly sensitive
                  personal information require higher levels of protection. We
                  need to have further justification for collecting, storing and
                  using this type of personal information. We have in place an
                  appropriate policy document and safeguards which we are
                  required by law to maintain when processing such data. We may
                  process special categories of personal information in the
                  following circumstances: 1. Where we need to carry out our
                  legal obligations or exercise rights in connection with
                  employment. 2. Where it is needed in the public interest, such
                  as for equal opportunities monitoring or in relation to our
                  pension scheme. Less commonly, we may process this type of
                  information where it is needed in relation to legal claims or
                  where it is needed to protect your interests (or someone
                  else's interests) and you are not capable of giving your
                  consent, or where you have already made the information
                  public. We may also process such information about members or
                  former members in the course of legitimate business activities
                  Our obligations as an employer We will use your particularly
                  sensitive personal information in the following ways: • We
                  will use information relating to leaves of absence, which may
                  include sickness absence or family related leaves, to comply
                  with employment and other laws. • We will use information
                  about your physical or mental health, or disability status, to
                  ensure your health and safety in the workplace and to assess
                  your fitness to work, to provide appropriate workplace
                  adjustments, to monitor and manage sickness absence and to
                  administer benefits. • We will use information about your race
                  or national or ethnic origin, religious, philosophical or
                  moral beliefs, or your sexual life or sexual orientation, to
                  ensure meaningful equal opportunity monitoring and reporting.
                  • We will use trade union membership information to pay trade
                  union premiums, register the status of a protected employee
                  and to comply with employment law obligations (where
                  applicable). Do we need your consent? We do not need your
                  consent if we use special categories of your personal
                  information in accordance with our written policy to carry out
                  our legal obligations or exercise specific rights in the field
                  of employment law. In limited circumstances, we may approach
                  you for your written consent to allow us to process certain
                  particularly sensitive data. If we do so, we will provide you
                  with full details of the information that we would like and
                  the reason we need it, so that you can carefully consider
                  whether you wish to consent. You should be aware that it is
                  not a condition of your contract with us that you agree to any
                  request for consent from us. Information about criminal
                  convictions We may only use information relating to criminal
                  convictions where the law allows us to do so. This will
                  usually be where such processing is necessary to carry out our
                  obligations. Less commonly, we may use information relating to
                  criminal convictions where it is necessary in relation to
                  legal claims, where it is necessary to protect your interests
                  (or someone else's interests) and you are not capable of
                  giving your consent, or where you have already made the
                  information public. We may also process such information about
                  members or former members in the course of legitimate business
                  activities. We will only collect information about criminal
                  convictions if it is appropriate given the nature of the role
                  and where we are legally able to do so. Where appropriate, we
                  will collect information about criminal convictions as part of
                  the recruitment process or we may be notified of such
                  information directly by you in the course of you working for
                  us. Data sharing We may have to share your data with third
                  parties, including third-party service providers and other
                  entities in the group. We require third parties to respect the
                  security of your data and to treat it in accordance with the
                  law. We do not transfer your personal information outside the
                  EU. Why might you share my personal information with third
                  parties? We will share your personal information with third
                  parties where required by law, where it is necessary to
                  administer the working relationship with you or where we have
                  another legitimate interest in doing so. Which third-party
                  service providers process my personal information? "Third
                  parties" includes third-party service providers (including
                  contractors and designated agents) and (where applicable)
                  other entities within our group. How secure is my information
                  with third-party service providers and other entities in our
                  group? All our third-party service providers and other
                  entities in the group are required to take appropriate
                  security measures to protect your personal information in line
                  with our policies. We do not allow our third-party service
                  providers to use your personal data for their own purposes. We
                  only permit them to process your personal data for specified
                  purposes and in accordance with our instructions. When might
                  you share my personal information with other entities in the
                  group? We will share your personal information with other
                  entities in our group as part of our regular reporting
                  activities on company performance, in the context of a
                  business reorganisation or group restructuring exercise, for
                  system maintenance support and hosting of data. What about
                  other third parties? We may share your personal information
                  with other third parties, for example in the context of the
                  possible sale or restructuring of the business. We may also
                  need to share your personal information with a regulator or to
                  otherwise comply with the law. Data security We have put in
                  place measures to protect the security of your information.
                  Third parties will only process your personal information on
                  our instructions and where they have agreed to treat the
                  information confidentially and to keep it secure. We have put
                  in place appropriate security measures to prevent your
                  personal information from being accidentally lost, used or
                  accessed in an unauthorised way, altered or disclosed. In
                  addition, we limit access to your personal information to
                  those employees, agents, contractors and other third parties
                  who have a business need to know. They will only process your
                  personal information on our instructions and they are subject
                  to a duty of confidentiality. We have put in place procedures
                  to deal with any suspected data security breach and will
                  notify you and any applicable regulator of a suspected breach
                  where we are legally required to do so. Data retention How
                  long will you use my information for? We will only retain your
                  personal information for as long as necessary to fulfil the
                  purposes we collected it for, including for the purposes of
                  satisfying any legal, accounting, or reporting requirements.
                  In order to determine the appropriate retention period for
                  personal data, we consider the amount, nature, and sensitivity
                  of the personal data, the potential risk of harm from
                  unauthorised use or disclosure of your personal data, the
                  purposes for which we process your personal data and whether
                  we can achieve those purposes through other means, and the
                  applicable legal requirements. In some circumstances we may
                  anonymise your personal information so that it can no longer
                  be associated with you, in which case we may use such
                  information without further notice to you. Once you are no
                  longer an employee, worker or contractor of the company we
                  will retain and securely destroy your personal information in
                  accordance with applicable laws and regulations. Rights of
                  access, correction, erasure, and restriction Your duty to
                  inform us of changes It is important that the personal
                  information we hold about you is accurate and current. Please
                  keep us informed if your personal information changes during
                  your working relationship with us. Your rights in connection
                  with personal information Under certain circumstances, by law
                  you have the right to: • Request access to your personal
                  information (commonly known as a "data subject access
                  request"). This enables you to receive a copy of the personal
                  information we hold about you and to check that we are
                  lawfully processing it. • Request correction of the personal
                  information that we hold about you. This enables you to have
                  any incomplete or inaccurate information we hold about you
                  corrected. • Request erasure of your personal information.
                  This enables you to ask us to delete or remove personal
                  information where there is no good reason for us continuing to
                  process it. You also have the right to ask us to delete or
                  remove your personal information where you have exercised your
                  right to object to processing (see below). • Object to
                  processing of your personal information where we are relying
                  on a legitimate interest (or those of a third party) and there
                  is something about your particular situation which makes you
                  want to object to processing on this ground. You also have the
                  right to object where we are processing your personal
                  information for direct marketing purposes. • Request the
                  restriction of processing of your personal information. This
                  enables you to ask us to suspend the processing of personal
                  information about you, for example if you want us to establish
                  its accuracy or the reason for processing it. • Request the
                  transfer of your personal information to another party. If you
                  want to review, verify, correct or request erasure of your
                  personal information, object to the processing of your
                  personal data, or request that we transfer a copy of your
                  personal information to another party, please contact Rapid Resourcing LTD
                  Recruitment Services Ltd in writing. No fee usually required
                  You will not have to pay a fee to access your personal
                  information (or to exercise any of the other rights). However,
                  we may charge a reasonable fee if your request for access is
                  clearly unfounded or excessive. Alternatively, we may refuse
                  to comply with the request in such circumstances. What we may
                  need from you We may need to request specific information from
                  you to help us confirm your identity and ensure your right to
                  access the information (or to exercise any of your other
                  rights). This is another appropriate security measure to
                  ensure that personal information is not disclosed to any
                  person who has no right to receive it. Right to withdraw
                  consent In the limited circumstances where you may have
                  provided your consent to the collection, processing and
                  transfer of your personal information for a specific purpose,
                  you have the right to withdraw your consent for that specific
                  processing at any time. In order to withdraw your consent,
                  please contact your Manager in writing (including the legal
                  basis for your belief that your consent can be withdrawn).
                  Once we have received notification that you have withdrawn
                  your consent, we will no longer process your information for
                  the purpose or purposes you originally agreed to, unless we
                  have another legitimate basis for doing so in law. Changes to
                  this privacy notice We reserve the right to update this
                  privacy notice at any time, and we will provide you with a new
                  privacy notice when we make any substantial updates. We may
                  also notify you in other ways from time to time about the
                  processing of your personal information. If you have any
                  questions about this privacy notice, please contact Rapid Resourcing LTD
                  Recruitment Services Ltd.
                </p>
                <Form.Group className="mb-3">
                  <Form.Control
                    type="date"
                    name="signedDate"
                    value={formData.signedDate}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Signed</Form.Label>
                  <div className="signature-pad">
                    <SignaturePad
                      ref={documentSigPadRef}
                      penColor="black"
                      canvasProps={{ className: "signature-canvas" }} // Customize the canvas appearance
                    />
                  </div>
                  {errors.documentSignature && (
                    <Alert variant="danger">{errors.documentSignature}</Alert>
                  )}
                  <Button
                    variant="secondary"
                    onClick={clearDocumentSignature}
                    className="mt-2"
                  >
                    Clear Signature
                  </Button>
                </Form.Group>
                <Button variant="primary" onClick={nextStep}>
                  Next
                </Button>
              </div>
            )}
            {step === 2 && (
              <div>
                <h3>Step 2: Sign and Submit</h3>
                <p>
                  By signing this page, I confirm that I fully understand the
                  content and agree to adhere to it at all times. I also agree
                  that all the details provided in this document are accurate
                  and complete to the best of my knowledge.
                </p>
                {/* Signature Pad Section */}
                <Form.Group className="mb-3">
                  <Form.Label>Signature</Form.Label>
                  <div className="signature-pad">
                    <SignaturePad
                      ref={sigPadRef}
                      penColor="black"
                      canvasProps={{ className: "signature-canvas" }} // Customize the canvas appearance
                    />
                  </div>
                  {errors.signature && (
                    <Alert variant="danger">{errors.signature}</Alert>
                  )}
                  <Button
                    variant="secondary"
                    onClick={clearSignature}
                    className="mt-2"
                  >
                    Clear Signature
                  </Button>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="consent"
                    label="Clear By ticking this box I consent to the company processing my
                data and the evidence of this document being stored within their
                system for the purpose of work finding services."
                    checked={formData.consent} // Use checked for checkboxes
                    onChange={handleChange}
                    isInvalid={!!errors.consent}
                  />
                  {errors.consent && (
                    <Alert variant="danger">{errors.consent}</Alert>
                  )}
                </Form.Group>
                <Button variant="secondary" onClick={prevStep} className="me-2">
                  Previous
                </Button>
                <Button variant="success" type="submit">
                  Submit
                </Button>
              </div>
            )}
          </Form>
        </>
      )}
    </Container>
  );
};
const mapStateToProps = (state) => ({
  currentDocument: state.user.currentDocument,
  documentList: state.user.documentList,
  user: state.user.loggedInUserData,
  sasToken: state.user.sasToken,
  ipAddress: state.user.ipAddress,
});
export default connect(mapStateToProps, { getSasToken })(GdprPolicy);
